/**
 * Collapsible vertical timeline for WebContainer prototyper build-stream (v2 segments).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import type {
  PrototyperBuildTimeline,
  PrototyperBuildTimelineV2,
  PrototyperSegmentKind,
  PrototyperTimelineItem,
  PrototyperTimelineSegment,
} from '@ideoz/core';
import {
  FRIENDLY_SEGMENT_LABEL,
  describeBuildActivity,
  friendlyItem,
  planningSentence,
  summarizeBuildTimeline,
} from '@ideoz/core';
import {
  ChevronDown,
  CheckCircle2,
  FileCode,
  Hammer,
  Loader2,
  Settings2,
  Wrench,
  Sparkles,
  XCircle,
  BookOpen,
  Search,
} from 'lucide-react';
import { cn } from '../atoms/utils';

const TERMINAL_SEGMENT_KINDS = new Set<PrototyperSegmentKind>(['finalizing', 'done']);

/** Mirrors `ThinkingPanel`'s `formatSeconds` — the "Thought for Xs" convention, extended to
 * minutes since a build can run well past 60s. */
function formatDuration(ms: number): string {
  const totalSeconds = Math.max(1, Math.round(ms / 1000));
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

/** Earliest `startedAt` / latest `endedAt` across all segments, for the header's elapsed time. */
function timelineSpan(segments: PrototyperTimelineSegment[]): {
  startedAt: number | null;
  endedAt: number | null;
} {
  let startedAt: number | null = null;
  let endedAt: number | null = null;
  for (const seg of segments) {
    if (seg.startedAt) {
      const t = new Date(seg.startedAt).getTime();
      if (!Number.isNaN(t) && (startedAt === null || t < startedAt)) startedAt = t;
    }
    if (seg.endedAt) {
      const t = new Date(seg.endedAt).getTime();
      if (!Number.isNaN(t) && (endedAt === null || t > endedAt)) endedAt = t;
    }
  }
  return { startedAt, endedAt };
}

/**
 * One-line "what's happening right now" for the collapsed header — the currently-running
 * item's title (e.g. "Write App.tsx") when available, else the running segment's label
 * (e.g. "Writing files"), else the last segment's label. Mirrors `activeSegmentLabel()` in
 * the web package's `prototyperBuildPhases.ts`, reimplemented locally since `packages/ui`
 * doesn't depend on `packages/web` (see `coerceTimelineV2` above for the same precedent).
 */
function activeStepLabel(segments: PrototyperTimelineSegment[]): string {
  if (segments.length === 0) return 'Working';
  const running = [...segments].reverse().find((s) => s.status === 'running');
  if (running) {
    const runningItem = running.items?.find((i) => i.status === 'running');
    if (runningItem?.title) return runningItem.title;
    return running.label;
  }
  return segments[segments.length - 1]?.label ?? 'Working';
}

/** True when finalizing/done appear in a v1 or v2 build timeline. */
export function timelineHasTerminalSegments(
  timeline: PrototyperBuildTimeline | null | undefined,
): boolean {
  if (!timeline) return false;
  if (timeline.schemaVersion === 2) {
    return timeline.segments.some((s) => TERMINAL_SEGMENT_KINDS.has(s.kind));
  }
  return timeline.phases.some((p) => p.id === 'finalizing' || p.id === 'done');
}

type SegmentIconStyle = {
  Icon: React.ComponentType<{ className?: string }>;
  iconCls: string;
  bgCls: string;
};

function getSegmentIconStyle(kind: PrototyperSegmentKind): SegmentIconStyle {
  switch (kind) {
    case 'planning':
      return {
        Icon: Settings2,
        iconCls: 'text-primary-600',
        bgCls: 'bg-primary-50 border-primary-200',
      };
    case 'exploring':
      return {
        Icon: BookOpen,
        iconCls: 'text-indigo-600',
        bgCls: 'bg-indigo-50 border-indigo-200',
      };
    case 'writing_files':
      return {
        Icon: FileCode,
        iconCls: 'text-primary-600',
        bgCls: 'bg-primary-50 border-primary-200',
      };
    case 'build':
      return {
        Icon: Hammer,
        iconCls: 'text-warning-600',
        bgCls: 'bg-warning-25 border-warning-200',
      };
    case 'fixing':
      return {
        Icon: Wrench,
        iconCls: 'text-indigo-600',
        bgCls: 'bg-indigo-50 border-indigo-200',
      };
    case 'finalizing':
      return {
        Icon: Sparkles,
        iconCls: 'text-success-600',
        bgCls: 'bg-success-50 border-success-200',
      };
    case 'done':
      return {
        Icon: CheckCircle2,
        iconCls: 'text-success-600',
        bgCls: 'bg-success-50 border-success-200',
      };
    default:
      return {
        Icon: Settings2,
        iconCls: 'text-gray-600',
        bgCls: 'bg-gray-50 border-gray-200',
      };
  }
}

function getItemIcon(toolName?: string): React.ComponentType<{ className?: string }> {
  if (toolName === 'readFile') return BookOpen;
  if (toolName === 'searchFiles') return Search;
  if (toolName === 'build') return Hammer;
  if (toolName === 'configureSession') return Settings2;
  return FileCode;
}

/** Migrate v1 phases → v2 segments inline when core helper is unavailable in UI bundle. */
function coerceTimelineV2(raw: PrototyperBuildTimeline): PrototyperBuildTimelineV2 {
  if (raw.schemaVersion === 2) return raw;
  const v1 = raw;
  const phaseToKind: Record<string, PrototyperSegmentKind> = {
    planning: 'planning',
    writing_files: 'writing_files',
    production_build: 'build',
    finalizing: 'finalizing',
    done: 'done',
  };
  const label: Record<PrototyperSegmentKind, string> = {
    planning: 'Planning',
    exploring: 'Exploring code',
    writing_files: 'Writing files',
    build: 'Build and type check',
    fixing: 'Fixing issues',
    finalizing: 'Finalizing',
    done: 'Done',
  };
  const segments: PrototyperTimelineSegment[] = [];
  for (const phase of v1.phases) {
    const kind = phaseToKind[phase.id];
    if (!kind) continue;
    if (kind === 'done' && phase.status === 'pending') continue;
    const items =
      phase.steps?.map((s) => ({
        key: s.key,
        title: s.title,
        status: s.status,
        toolName: s.toolName,
      })) ?? [];
    if (items.length === 0 && phase.status === 'pending' && kind !== 'done') continue;
    segments.push({
      key: `${kind}-m-${segments.length}`,
      kind,
      label: label[kind],
      status: phase.status,
      items: items.length ? items : undefined,
      startedAt: phase.startedAt,
      endedAt: phase.endedAt,
    });
  }
  return { schemaVersion: 2, collapsedDefault: v1.collapsedDefault, segments };
}

function ItemStatusIcon({
  status,
  isLive,
}: {
  status: PrototyperTimelineItem['status'];
  isLive: boolean;
}) {
  if (status === 'running' && isLive) {
    return <Loader2 className="h-3 w-3 shrink-0 animate-spin text-primary-500" />;
  }
  if (status === 'error') {
    return <XCircle className="h-3 w-3 shrink-0 text-error-500" />;
  }
  if (status === 'done') {
    return <CheckCircle2 className="h-3 w-3 shrink-0 text-success-500" />;
  }
  return null;
}

/** Seconds since `startedAt`, ticking once a second while `live`. */
function useElapsedSeconds(startedAt: string | null | undefined, live: boolean): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [live]);
  if (!live || !startedAt) return null;
  const t = new Date(startedAt).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((now - t) / 1000));
}

/**
 * The founder view lists each file once: three patches to one page read as
 * "Updated ShiftHandoverBoardPage · 3 edits", not three identical lines. The item kept is the
 * last one, so a running or failed state shows.
 */
function mergeRepeatedItems(
  items: PrototyperTimelineItem[],
): Array<{ item: PrototyperTimelineItem; title: string; count: number }> {
  const merged: Array<{ item: PrototyperTimelineItem; title: string; count: number }> = [];
  const byTitle = new Map<string, number>();
  for (const item of items) {
    const title = friendlyItem(item).title;
    const at = byTitle.get(title);
    if (at === undefined) {
      byTitle.set(title, merged.length);
      merged.push({ item, title, count: 1 });
    } else {
      merged[at] = { item, title, count: merged[at]!.count + 1 };
    }
  }
  return merged;
}

/** Stages whose content is the change itself; planning and reading stay muted. */
const EMPHASISED_KINDS = new Set<PrototyperSegmentKind>(['writing_files', 'fixing']);

/**
 * One stage in the founder's words (`FRIENDLY_SEGMENT_LABEL`): the plan as one sentence, reading
 * folded into "Read 2 files", files as Updated / Wrote / Removed, the check as Passed or the
 * problem it found. The running stage shows its elapsed time. `technical` shows the stage as the
 * timeline stores it: paths, line counts, mode and language.
 */
function SegmentRow({
  segment,
  isLive,
  isLast,
  technical,
}: {
  segment: PrototyperTimelineSegment;
  isLive: boolean;
  isLast: boolean;
  technical: boolean;
}) {
  const { Icon, iconCls, bgCls } = getSegmentIconStyle(segment.kind);
  const showRunning = segment.status === 'running' && isLive;
  const elapsed = useElapsedSeconds(segment.startedAt, showRunning);
  const failed = segment.status === 'error';

  const label = technical ? segment.label : (FRIENDLY_SEGMENT_LABEL[segment.kind] ?? segment.label);
  const items = segment.items ?? [];

  let body: string | null = null;
  let list: PrototyperTimelineItem[] = [];
  if (technical) {
    body = segment.body ?? null;
    list = segment.kind !== 'planning' ? items : [];
  } else if (segment.kind === 'planning') {
    body = planningSentence(segment) ?? null;
  } else if (segment.kind === 'exploring') {
    const running = items.find((i) => i.status === 'running') ?? items[items.length - 1];
    body = showRunning
      ? `${running ? friendlyItem(running).action : 'Reading your prototype'}…`
      : items.length === 1
        ? 'Read 1 file'
        : `Read ${items.length} files`;
  } else if (segment.kind === 'fixing') {
    body = segment.body ?? null;
  } else {
    list = items;
  }

  return (
    <div className="relative flex gap-3 pb-4 last:pb-0">
      {!isLast && (
        <span className="absolute left-[15px] top-8 bottom-0 w-px bg-gray-200" aria-hidden />
      )}
      <div
        className={cn(
          'relative z-[1] flex h-8 w-8 shrink-0 items-center justify-center rounded-full border',
          bgCls,
        )}
      >
        {showRunning ? (
          <Loader2 className={cn('h-4 w-4 animate-spin', iconCls)} />
        ) : failed ? (
          <XCircle className="h-4 w-4 text-error-500" />
        ) : segment.status === 'done' ? (
          <CheckCircle2 className={cn('h-4 w-4', iconCls)} />
        ) : (
          <Icon className={cn('h-4 w-4', iconCls)} />
        )}
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'text-sm',
              EMPHASISED_KINDS.has(segment.kind) || failed || showRunning
                ? 'font-semibold text-foreground'
                : 'font-medium text-foreground/80',
              failed && 'text-error-700',
            )}
          >
            {label}
          </span>
          {elapsed != null ? (
            <span className="text-[11px] tabular-nums text-muted-foreground">{elapsed}s</span>
          ) : null}
        </div>
        {body ? (
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground whitespace-pre-wrap">
            {body}
          </p>
        ) : null}
        {list.length > 0 ? (
          <ul className="mt-2 space-y-1.5">
            {(technical
              ? list.map((item) => ({ item, title: item.title, count: 1 }))
              : mergeRepeatedItems(list)
            ).map(({ item, title: baseTitle, count }) => {
              const ItemIcon = getItemIcon(item.toolName);
              const title = count > 1 ? `${baseTitle} · ${count} edits` : baseTitle;
              // The founder view keeps one detail: why a check failed.
              const detail = technical || item.status === 'error' ? item.detail : undefined;
              return (
                <li key={item.key} className="flex items-start gap-2 text-xs">
                  <ItemStatusIcon status={item.status} isLive={isLive} />
                  {!item.status ||
                  (item.status !== 'running' && item.status !== 'error' && item.status !== 'done') ? (
                    <ItemIcon className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <span
                      className={cn(
                        'text-foreground/90',
                        item.status === 'error' && 'text-error-600',
                      )}
                    >
                      {title}
                    </span>
                    {detail ? (
                      <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                        {detail}
                      </span>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

export interface PrototyperReasoningTimelineProps {
  timeline: PrototyperBuildTimeline | null | undefined;
  isLive?: boolean;
  showTerminalPhases?: boolean;
  /** When this changes (e.g. message id), collapse state resets back to collapsed. */
  resetKey?: string;
  /**
   * The turn's build stopped before it passed, decided by the host from the turn's version (the
   * same test as the pill's "Didn't finish"). Not read off the timeline: a finished build can
   * persist a timeline whose last step is still marked as an error.
   */
  didNotFinish?: boolean;
  className?: string;
}

export const PrototyperReasoningTimeline: React.FC<PrototyperReasoningTimelineProps> = ({
  timeline,
  isLive = false,
  showTerminalPhases = false,
  resetKey,
  className,
  didNotFinish = false,
}) => {
  const displayTimeline = useMemo(
    () => (timeline ? coerceTimelineV2(timeline) : null),
    [timeline],
  );

  /**
   * Elapsed time is only meaningful for a build this component actually watched happen live —
   * a *restored* timeline's `startedAt`/`endedAt` are whatever the backend persisted, which can
   * span far more than actual generation time (e.g. the planning segment's `startedAt` predates
   * a clarifying back-and-forth before the build turn even started). Tracked per-message via
   * `resetKey` so switching to a different message doesn't carry over a stale "was live" flag.
   */
  const wasLiveRef = useRef(false);

  // Never auto-expand, live or restored — mirrors `ThinkingPanel`. The user clicks the
  // header to review the trail; a running build just updates the one-line summary in place.
  const [expanded, setExpanded] = useState(false);
  /** Paths, line counts, mode and language, as the timeline used to show them. */
  const [technical, setTechnical] = useState(false);

  const prevResetKeyRef = useRef(resetKey);
  useEffect(() => {
    if (prevResetKeyRef.current === resetKey) return;
    prevResetKeyRef.current = resetKey;
    wasLiveRef.current = isLive;
    setExpanded(false);
    setTechnical(false);
  }, [resetKey, isLive]);

  if (isLive) wasLiveRef.current = true;

  const toggleExpanded = () => setExpanded((prev) => !prev);

  const visibleSegments = useMemo(() => {
    if (!displayTimeline?.segments?.length) return [];
    return displayTimeline.segments.filter(
      (seg) => showTerminalPhases || !TERMINAL_SEGMENT_KINDS.has(seg.kind),
    );
  }, [displayTimeline, showTerminalPhases]);

  const showSpecCoverageGaps = useMemo(() => {
    if (!displayTimeline?.specCoverage || displayTimeline.specCoverage.ok) return false;
    return displayTimeline.specCoverage.gaps.length > 0;
  }, [displayTimeline]);

  // Elapsed time is a one-shot value shown only once the build is done ("Built in Xs") —
  // mirroring `ThinkingPanel`'s "Thought for Xs". While live we show the current step instead
  // (see `activeStepLabel`), so there's no need to tick this every second.
  const { startedAt: spanStartedAt, endedAt: spanEndedAt } = useMemo(
    () => timelineSpan(displayTimeline?.segments ?? []),
    [displayTimeline],
  );
  const elapsedLabel = useMemo(() => {
    // Never for a restored/historical timeline — see the note on `wasLiveRef` above.
    if (isLive || !wasLiveRef.current) return null;
    if (spanStartedAt == null || spanEndedAt == null) return null;
    return formatDuration(Math.max(0, spanEndedAt - spanStartedAt));
  }, [isLive, spanStartedAt, spanEndedAt]);

  const liveStepLabel = useMemo(
    () =>
      describeBuildActivity(displayTimeline)?.action ??
      activeStepLabel(displayTimeline?.segments ?? []),
    [displayTimeline],
  );
  const summary = useMemo(() => summarizeBuildTimeline(displayTimeline), [displayTimeline]);
  const changedLabel =
    summary.filesChanged === 0
      ? null
      : summary.filesChanged === 1
        ? '1 file changed'
        : `${summary.filesChanged} files changed`;

  if (!visibleSegments.length) {
    if (!isLive) return null;
    return (
      <div
        className={cn('inline-flex text-sm w-full min-w-0', className)}
        data-testid="prototyper-reasoning-timeline-empty"
      >
        <div className="flex items-center gap-2 px-2.5 py-1.5 text-gray-600 dark:text-gray-300 animate-pulse">
          <Loader2 size={12} className="animate-spin text-primary-500" />
          <span>{liveStepLabel}…</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn('inline-flex flex-col text-sm w-full min-w-0', className)}
      data-testid="prototyper-reasoning-timeline"
    >
      <button
        type="button"
        onClick={toggleExpanded}
        aria-expanded={expanded}
        className="flex items-center gap-2 px-2.5 py-1.5 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
      >
        {isLive ? (
          <Loader2 size={12} className="animate-spin text-primary-500" />
        ) : (
          <Sparkles size={12} className="text-gray-400 dark:text-gray-500" />
        )}
        <span className="truncate">
          {isLive
            ? `${liveStepLabel}…`
            : didNotFinish
              ? "Build didn't finish"
              : [elapsedLabel ? `Built in ${elapsedLabel}` : 'Built the prototype', changedLabel]
                  .filter(Boolean)
                  .join(' · ')}
        </span>
        <ChevronDown
          size={12}
          className={cn(
            'shrink-0 text-gray-400 dark:text-gray-500 transition-transform',
            expanded && 'rotate-180',
          )}
        />
      </button>
      {expanded ? (
        // `isolate`: the step circles' z-index lifts them over the connecting line only; without
        // it they were drawn over the chat composer when the list scrolled under it.
        <div className="isolate mx-3 mt-1 max-h-72 overflow-y-auto rounded-md bg-gray-100 px-4 py-3 text-gray-500 dark:text-gray-400">
          {showSpecCoverageGaps ? (
            <div
              className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
              data-testid="prototyper-spec-coverage-gaps"
            >
              <p className="font-medium">Spec coverage gaps</p>
              <ul className="mt-1 list-disc pl-4">
                {displayTimeline!.specCoverage!.gaps.map((gap) => (
                  <li key={gap}>{gap}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {visibleSegments.map((segment, idx) => (
            <SegmentRow
              key={segment.key}
              segment={segment}
              isLive={isLive}
              isLast={idx === visibleSegments.length - 1}
              technical={technical}
            />
          ))}
          <button
            type="button"
            onClick={() => setTechnical((v) => !v)}
            className="mt-3 text-[11px] font-medium text-gray-500 underline-offset-2 hover:text-gray-700 hover:underline dark:hover:text-gray-300"
            data-testid="prototyper-timeline-technical-toggle"
          >
            {technical ? 'Hide technical details' : 'Show technical details'}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default PrototyperReasoningTimeline;
